import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { useAppStore } from '../stores/app-store';
import { useSubscriptionStore, PLAN_NAMES } from '../stores/subscription-store';
import {
  BookOpen,
  ScrollText,
  GraduationCap,
  Heart,
  File,
  Upload,
  AlertTriangle,
  Sparkles,
  Check,
  Loader2,
  ArrowRight,
} from 'lucide-react';
import { ImportBookModal } from './import-book-modal';
import { useIsMobile } from '@/hooks/use-mobile';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import { Drawer, DrawerContent, DrawerTitle } from './ui/drawer';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { cn } from '../lib/utils';
import { notify } from '../lib/toast';

interface TemplateModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const templates = [
  {
    id: 'novel',
    icon: BookOpen,
    gradient: 'from-violet-500 to-purple-600',
    bgGradient: 'from-violet-500/10 to-purple-600/10',
    chapters: 10,
  },
  {
    id: 'novella',
    icon: ScrollText,
    gradient: 'from-emerald-500 to-teal-600',
    bgGradient: 'from-emerald-500/10 to-teal-600/10',
    chapters: 5,
  },
  {
    id: 'nonfiction',
    icon: GraduationCap,
    gradient: 'from-amber-500 to-orange-600',
    bgGradient: 'from-amber-500/10 to-orange-600/10',
    chapters: 11,
  },
  {
    id: 'memoir',
    icon: Heart,
    gradient: 'from-rose-500 to-pink-600',
    bgGradient: 'from-rose-500/10 to-pink-600/10',
    chapters: 8,
  },
  {
    id: 'blank',
    icon: File,
    gradient: 'from-slate-500 to-slate-600',
    bgGradient: 'from-slate-500/10 to-slate-600/10',
    chapters: 1,
  },
] as const;

type TemplateId = (typeof templates)[number]['id'];

export function TemplateModal({ open, onOpenChange }: TemplateModalProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const createProject = useAppStore((s) => s.createProject);
  const { canCreateProject, usage, plan, fetchUsage } = useSubscriptionStore();
  const isMobile = useIsMobile();
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateId>('novel');
  const [title, setTitle] = useState('');
  const [author, setAuthor] = useState('');
  const [showImport, setShowImport] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [step, setStep] = useState<'template' | 'details'>('template');

  useEffect(() => {
    fetchUsage();
  }, [fetchUsage]);

  // Reset step when modal opens/closes
  useEffect(() => {
    if (!open) {
      setStep('template');
      setTitle('');
      setAuthor('');
    }
  }, [open]);

  const canCreate = canCreateProject();

  if (showImport) {
    return <ImportBookModal open={open} onOpenChange={onOpenChange} />;
  }

  const handleCreate = async () => {
    if (!canCreate) {
      notify.limitReached('projetos', PLAN_NAMES[plan]);
      return;
    }

    setIsCreating(true);
    try {
      const projectId = await createProject(
        {
          title: title || t('projects.untitled'),
          author,
        },
        selectedTemplate
      );
      onOpenChange(false);
      navigate(`/app/editor/${projectId}`);
    } catch (error) {
      notify.error(t('errors.saveFailed'));
    } finally {
      setIsCreating(false);
    }
  };

  const selectedTemplateData = templates.find((tmpl) => tmpl.id === selectedTemplate)!;

  // ── Shared: header section ────────────────────────────────────────────────────

  const renderHeader = (TitleComponent: typeof DialogTitle | typeof DrawerTitle) => (
    <div className="relative bg-gradient-to-br from-primary/5 via-primary/10 to-transparent p-6 pb-4 flex-shrink-0">
      <div>
        <TitleComponent className="text-xl font-semibold text-foreground">
          {t('templates.title')}
        </TitleComponent>
        <p className="text-sm text-muted-foreground mt-0.5">
          {step === 'template'
            ? t('templateModal.stepTemplateSubtitle', 'Escolha um modelo para começar seu novo livro')
            : t('templateModal.stepDetailsSubtitle', 'Defina os detalhes do seu projeto')
          }
        </p>
      </div>

      {/* Step indicator */}
      <div className="flex items-center gap-3 mt-4">
        <div className={cn(
          'flex items-center gap-2 text-sm font-medium transition-colors',
          step === 'template' ? 'text-primary' : 'text-primary/60',
        )}>
          <span className={cn(
            'flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold',
            step === 'template'
              ? 'bg-primary text-primary-foreground'
              : 'bg-primary/20 text-primary',
          )}>
            {step === 'details' ? <Check className="h-3.5 w-3.5" /> : '1'}
          </span>
          <span>{t('templateModal.stepModel', 'Modelo')}</span>
        </div>
        <div className="w-12 h-px bg-border" />
        <div className={cn(
          'flex items-center gap-2 text-sm font-medium transition-colors',
          step === 'details' ? 'text-primary' : 'text-muted-foreground',
        )}>
          <span className={cn(
            'flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold',
            step === 'details'
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted text-muted-foreground',
          )}>
            2
          </span>
          <span>{t('templateModal.stepDetails', 'Detalhes')}</span>
        </div>
      </div>
    </div>
  );

  // ── Shared: template grid (step 1) ───────────────────────────────────────────

  const renderTemplateStep = (mobile: boolean) => (
    <motion.div
      key="template"
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.2 }}
    >
      {/* Template Grid — 2 cols on mobile for comfortable touch, 5 on desktop */}
      <div className={cn(
        'grid gap-3',
        mobile ? 'grid-cols-2' : 'grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4',
      )}>
        {templates.map((template) => {
          const Icon = template.icon;
          const isSelected = selectedTemplate === template.id;

          return (
            <motion.button
              key={template.id}
              onClick={() => setSelectedTemplate(template.id)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={cn(
                'relative flex flex-col items-center gap-3 rounded-xl border-2 transition-all text-left group min-w-0',
                mobile ? 'p-4 py-5' : 'p-4',
                isSelected
                  ? 'border-primary bg-primary/5 shadow-lg shadow-primary/10'
                  : 'border-border hover:border-primary/30 hover:bg-muted/50',
              )}
            >
              {/* Selection indicator */}
              {isSelected && (
                <motion.div
                  layoutId="selectedIndicator"
                  className="absolute -top-1.5 -right-1.5 p-1 rounded-full bg-primary"
                >
                  <Check className="h-3 w-3 text-primary-foreground" />
                </motion.div>
              )}

              {/* Icon with gradient background */}
              <div className={cn(
                'p-3 rounded-xl bg-gradient-to-br transition-transform group-hover:scale-110',
                template.bgGradient,
              )}>
                <div className={cn(
                  'rounded-lg bg-gradient-to-br',
                  mobile ? 'p-2.5' : 'p-2',
                  template.gradient,
                )}>
                  <Icon className={cn('text-white', mobile ? 'h-6 w-6' : 'h-5 w-5')} />
                </div>
              </div>

              <div className="text-center w-full">
                <p className="font-medium text-sm text-foreground truncate">
                  {t(`templates.${template.id}`)}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {template.chapters}{' '}
                  {template.chapters === 1
                    ? t('templateModal.sectionSingular', 'seção')
                    : t('templateModal.sectionPlural', 'seções')}
                </p>
              </div>
            </motion.button>
          );
        })}
      </div>

      {/* Import option */}
      <div className="mt-4 pt-4 border-t">
        <button
          onClick={() => setShowImport(true)}
          className={cn(
            'w-full flex items-center justify-center gap-2 rounded-xl border border-dashed border-border',
            'hover:border-primary/50 hover:bg-muted/50 transition-colors text-muted-foreground hover:text-foreground',
            mobile ? 'p-4' : 'p-3',
          )}
        >
          <Upload className="h-4 w-4" />
          <span className="text-sm font-medium">
            {t('import.importFile', 'Importar arquivo existente')}
          </span>
          <span className="text-xs text-muted-foreground">
            (.docx, .pdf, .txt, .md)
          </span>
        </button>
      </div>
    </motion.div>
  );

  // ── Shared: details form (step 2) ────────────────────────────────────────────

  const renderDetailsStep = (mobile: boolean) => (
    <motion.div
      key="details"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
      transition={{ duration: 0.2 }}
      className="space-y-6"
    >
      {/* Layout: stacked on mobile, two-column on desktop */}
      <div className={cn(mobile ? 'space-y-4' : 'grid md:grid-cols-2 gap-6')}>
        {/* Selected template preview */}
        <div className={cn(
          'flex items-center gap-4 p-4 rounded-xl bg-gradient-to-br',
          mobile ? 'flex-row' : 'flex-col justify-center p-6',
          selectedTemplateData.bgGradient,
        )}>
          <div className={cn(
            'rounded-xl bg-gradient-to-br flex-shrink-0',
            mobile ? 'p-3' : 'p-4',
            selectedTemplateData.gradient,
          )}>
            {(() => {
              const Icon = selectedTemplateData.icon;
              return <Icon className={cn('text-white', mobile ? 'h-6 w-6' : 'h-8 w-8')} />;
            })()}
          </div>
          <div className={cn('min-w-0', mobile ? '' : 'text-center')}>
            <p className="font-semibold text-foreground">
              {t(`templates.${selectedTemplate}`)}
            </p>
            <p className="text-sm text-muted-foreground">
              {t('templateModal.sectionsIncluded', '{{count}} seções incluídas', { count: selectedTemplateData.chapters })}
            </p>
          </div>
          <button
            onClick={() => setStep('template')}
            className={cn(
              'text-xs text-primary hover:underline flex-shrink-0',
              mobile ? 'ml-auto' : 'mt-1',
            )}
          >
            {t('templateModal.change', 'Alterar')}
          </button>
        </div>

        {/* Form fields */}
        <div className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="title" className="text-sm font-medium text-foreground">
              {t('templateModal.bookTitleLabel', 'Título do Livro')}
            </label>
            <Input
              id="title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('projects.untitled')}
              className={cn(mobile ? 'h-12 text-base' : 'h-11')}
              autoFocus
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="author" className="text-sm font-medium text-foreground">
              {t('templateModal.authorNameLabel', 'Nome do Autor')}
            </label>
            <Input
              id="author"
              type="text"
              value={author}
              onChange={(e) => setAuthor(e.target.value)}
              placeholder={t('templateModal.authorNamePlaceholder', 'Seu nome')}
              className={cn(mobile ? 'h-12 text-base' : 'h-11')}
            />
          </div>
        </div>
      </div>

      {/* Limit Warning */}
      {!canCreate && usage && (
        <div className="flex items-center gap-2 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-700 dark:text-amber-400 text-sm">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          <span>
            {t('templateModal.limitReached', 'Limite atingido: {{used}}/{{limit}} projetos.', {
              used: usage.projects.used,
              limit: usage.projects.limit,
            })}{' '}
            <a href="/pricing" className="underline font-medium">
              {t('templateModal.upgrade', 'Fazer upgrade')}
            </a>
          </span>
        </div>
      )}
    </motion.div>
  );

  // ── Shared: footer buttons ────────────────────────────────────────────────────

  const renderFooter = (mobile: boolean) => (
    <div className={cn(
      'flex items-center justify-between border-t bg-muted/30 flex-shrink-0',
      mobile ? 'p-4 gap-3' : 'p-4',
    )}>
      {step === 'template' ? (
        <>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className={cn(mobile && 'h-12 px-5 text-base')}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={() => setStep('details')}
            className={cn('gap-2', mobile && 'h-12 px-6 text-base flex-1')}
          >
            {t('templateModal.continue', 'Continuar')}
            <ArrowRight className="h-4 w-4" />
          </Button>
        </>
      ) : (
        <>
          <Button
            variant="ghost"
            onClick={() => setStep('template')}
            className={cn(mobile && 'h-12 px-5 text-base')}
          >
            {t('templateModal.back', 'Voltar')}
          </Button>
          <Button
            onClick={handleCreate}
            disabled={!canCreate || isCreating}
            className={cn('gap-2 min-w-32', mobile && 'h-12 px-6 text-base flex-1')}
          >
            {isCreating ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('templateModal.creating', 'Criando...')}
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4" />
                {t('templateModal.createBook', 'Criar Livro')}
              </>
            )}
          </Button>
        </>
      )}
    </div>
  );

  // ── Mobile: vaul Drawer direction="bottom" ────────────────────────────────────

  if (isMobile) {
    return (
      <Drawer
        open={open}
        onOpenChange={(isOpen) => { if (!isOpen) onOpenChange(false); }}
        direction="bottom"
      >
        <DrawerContent
          className={cn(
            'bg-background text-foreground border-border',
            'max-h-[90dvh] flex flex-col',
          )}
        >
          {/* Drag handle is rendered by DrawerContent automatically */}
          {renderHeader(DrawerTitle)}

          {/* Scrollable content area */}
          <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 py-4">
            <AnimatePresence mode="wait">
              {step === 'template'
                ? renderTemplateStep(true)
                : renderDetailsStep(true)
              }
            </AnimatePresence>
          </div>

          {renderFooter(true)}
        </DrawerContent>
      </Drawer>
    );
  }

  // ── Desktop: Dialog modal centralizado ────────────────────────────────────────

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton
        className="gap-0 overflow-hidden p-0 sm:max-w-4xl"
      >
        {renderHeader(DialogTitle)}

        {/* Content */}
        <div className="p-6">
          <AnimatePresence mode="wait">
            {step === 'template'
              ? renderTemplateStep(false)
              : renderDetailsStep(false)
            }
          </AnimatePresence>
        </div>

        {renderFooter(false)}
      </DialogContent>
    </Dialog>
  );
}
